import type { OrgRole } from "./adminTypes";
import { postContactsDirectoryOp, type ExternalContactRow, type UserDirectoryRow } from "@/lib/contactsDirectoryClient";
import { directoryTopicsToCommaString } from "./adminContactsDirectoryUtils";
import { invitePrecheck, sendInvite } from "./adminContactsInviteClient";

type Toast = (msg: string, variant?: "ok" | "err") => void;

type Params = {
  organizationId: string;
  setDirectoryMode: (m: "contacts" | "general_support" | "add_contact" | "tags" | "org_tree") => void;
  authToken: string | undefined;
  inviteEmail: string;
  inviteRole: OrgRole;
  inviteTarget: ExternalContactRow | null;
  setInviteBusy: (v: boolean) => void;
  setInviteTarget: (v: ExternalContactRow | null) => void;
  setInviteEmail: (v: string) => void;
  migrateTarget: ExternalContactRow | null;
  migrateEmail: string;
  migrateProfileId: string;
  setMigrateTarget: (v: ExternalContactRow | null) => void;
  setMigrateEmail: (v: string) => void;
  setMigrateProfileId: (v: string) => void;
  extForm: any;
  setExtForm: (v: any) => void;
  setEditingUserRow: (v: UserDirectoryRow | null) => void;
  isLikelyValidEmail: (v: string) => boolean;
  formatUnknownError: (e: unknown) => string;
  createEmptyExternalForm: () => any;
  load: () => Promise<void>;
  pathSource: string;
  pathTarget: string;
  setPathSource: (v: string) => void;
  setPathTarget: (v: string) => void;
  topicSource: string;
  topicTarget: string;
  setTopicSource: (v: string) => void;
  setTopicTarget: (v: string) => void;
  generalFallbackChoice: string;
  setGeneralFallbackChoice: (v: string) => void;
  setGeneralFallbackBusy: (v: boolean) => void;
  toast: Toast;
};

export function useAdminContactsDirectoryActions(p: Params) {
  function editExternal(row: ExternalContactRow) {
    const topics = directoryTopicsToCommaString(row.topics);
    p.setEditingUserRow(null);
    p.setDirectoryMode("add_contact");
    p.setExtForm({ id: row.id, name: row.name, email: row.email ?? "", phone: row.phone ?? "", company: row.company ?? "", title: row.title ?? "", department: row.department ?? "", contact_path: row.contact_path ?? "", visibility_level: String(row.visibility_level ?? 1), max_downstream_visibility_level: row.max_downstream_visibility_level == null ? "" : String(row.max_downstream_visibility_level), location_id: row.location_id ?? "", scope: row.scope ?? "org", topics, affiliation: String(row.affiliation || "org_affiliated"), hierarchy_participation: String(row.hierarchy_participation || "full"), reports_to_external_id: row.reports_to_profile_id ?? row.reports_to_external_id ?? "", allow_vendor_hierarchy_override: false, allow_vendor_reporting_line: false });
  }

  function openUserDirectoryEdit(row: UserDirectoryRow) {
    const topics = directoryTopicsToCommaString(row.topics);
    const profile = row.profiles;
    const profilePhone =
      String(profile?.personal_phone || "").trim() || String(profile?.work_phone || "").trim() || "";
    p.setEditingUserRow(row);
    p.setDirectoryMode("add_contact");
    p.setExtForm({
      id: row.id,
      name: String(profile?.full_name || profile?.display_name || profile?.email || row.profile_id || "").trim(),
      email: profile?.email ?? "",
      phone: profilePhone,
      company: "",
      title: "",
      department: row.department ?? "",
      contact_path: row.contact_path ?? "",
      visibility_level: String(row.visibility_level ?? 1),
      max_downstream_visibility_level: row.max_downstream_visibility_level == null ? "" : String(row.max_downstream_visibility_level),
      location_id: row.location_id ?? "",
      scope: row.scope ?? "org",
      topics,
      affiliation: String(row.affiliation || "org_affiliated"),
      hierarchy_participation: String(row.hierarchy_participation || "full"),
      reports_to_external_id: row.reports_to_profile_id ?? row.reports_to_external_id ?? "",
      allow_vendor_hierarchy_override: false,
      allow_vendor_reporting_line: false,
    });
  }

  async function runInviteLink() {
    if (!p.inviteTarget) return;
    if (!p.authToken) return p.toast("Not authenticated.", "err");
    const email = p.inviteEmail.trim();
    if (!email) return p.toast("Email required.", "err");
    if (!p.isLikelyValidEmail(email)) return p.toast("Please enter a valid email address.", "err");
    p.setInviteBusy(true);
    try {
      const potentialMatches = await invitePrecheck({ token: p.authToken, email, orgId: p.organizationId });
      let allowPotentialDuplicate = false;
      if (potentialMatches.length > 0) {
        const labels = potentialMatches.map((m) => String(m.full_name || m.display_name || m.email || "Unknown member")).join(", ");
        allowPotentialDuplicate = window.confirm(`Potential duplicate invite detected for: ${labels}. Continue anyway and create a new user record?`);
        if (!allowPotentialDuplicate) return;
      }
      const { migrateWarning } = await sendInvite({ token: p.authToken, email, orgId: p.organizationId, role: p.inviteRole, allowPotentialDuplicate, migrateExternalContactId: p.inviteTarget.id });
      p.toast(`Invite sent to ${email}.${migrateWarning ? ` Directory migration note: ${migrateWarning}` : ""}`, migrateWarning ? "err" : "ok");
      p.setInviteTarget(null);
      p.setInviteEmail("");
      await p.load();
    } catch (e: unknown) { p.toast(p.formatUnknownError(e), "err"); } finally { p.setInviteBusy(false); }
  }

  async function runMigrateManual() {
    if (!p.migrateTarget) return;
    const em = p.migrateEmail.trim(); const pid = p.migrateProfileId.trim();
    if (!em && !pid) return p.toast("Provide profile email or profile UUID.", "err");
    if (em && !p.isLikelyValidEmail(em)) return p.toast("Please enter a valid profile email.", "err");
    try {
      await postContactsDirectoryOp(p.organizationId, { op: "migrate_external", external_id: p.migrateTarget.id, email: em || undefined, profile_id: pid || undefined });
      p.toast("Migrated to user directory (external row deactivated).");
      p.setMigrateTarget(null); p.setMigrateEmail(""); p.setMigrateProfileId("");
      await p.load();
    } catch (e: unknown) { p.toast(p.formatUnknownError(e), "err"); }
  }

  async function mergePath() {
    if (!p.pathSource || !p.pathTarget.trim()) return p.toast("Pick source and target path.", "err");
    try {
      await postContactsDirectoryOp(p.organizationId, { op: "merge_path", from_path: p.pathSource, to_path: p.pathTarget.trim() });
      p.toast("Path merged."); p.setPathSource(""); p.setPathTarget(""); await p.load();
    } catch (e: unknown) { p.toast(p.formatUnknownError(e), "err"); }
  }
  async function deletePathTag() {
    if (!p.pathSource) return p.toast("Pick a path to delete.", "err");
    if (!window.confirm(`Delete path tag '${p.pathSource}' from all contacts?`)) return;
    try { await postContactsDirectoryOp(p.organizationId, { op: "delete_path_tag", path: p.pathSource }); p.toast("Path tag deleted."); p.setPathSource(""); await p.load(); } catch (e: unknown) { p.toast(p.formatUnknownError(e), "err"); }
  }
  async function mergeTopic() {
    if (!p.topicSource || !p.topicTarget.trim()) return p.toast("Pick source and target topic.", "err");
    try { await postContactsDirectoryOp(p.organizationId, { op: "merge_topic", from_topic: p.topicSource, to_topic: p.topicTarget.trim() }); p.toast("Topic merged."); p.setTopicSource(""); p.setTopicTarget(""); await p.load(); } catch (e: unknown) { p.toast(p.formatUnknownError(e), "err"); }
  }
  async function deleteTopicTag() {
    if (!p.topicSource) return p.toast("Pick a topic to delete.", "err");
    if (!window.confirm(`Delete topic tag '${p.topicSource}' from all contacts?`)) return;
    try { await postContactsDirectoryOp(p.organizationId, { op: "delete_topic_tag", topic: p.topicSource }); p.toast("Topic tag deleted."); p.setTopicSource(""); await p.load(); } catch (e: unknown) { p.toast(p.formatUnknownError(e), "err"); }
  }

  async function saveGeneralFallback() {
    if (!p.generalFallbackChoice) return p.toast("Pick a fallback contact first.", "err");
    const [source, source_id] = p.generalFallbackChoice.split(":");
    if (!source || !source_id) return p.toast("Invalid fallback selection.", "err");
    p.setGeneralFallbackBusy(true);
    try { await postContactsDirectoryOp(p.organizationId, { op: "set_general_fallback", source, source_id }); p.toast("General support fallback updated."); await p.load(); } catch (e: unknown) { p.toast(p.formatUnknownError(e), "err"); } finally { p.setGeneralFallbackBusy(false); }
  }
  async function clearGeneralFallback() {
    p.setGeneralFallbackBusy(true);
    try { await postContactsDirectoryOp(p.organizationId, { op: "clear_general_fallback" }); p.toast("General support fallback cleared."); p.setGeneralFallbackChoice(""); await p.load(); } catch (e: unknown) { p.toast(p.formatUnknownError(e), "err"); } finally { p.setGeneralFallbackBusy(false); }
  }

  async function suggestMissingTopicTagsFromKb(): Promise<string[]> {
    const out = await postContactsDirectoryOp(p.organizationId, {
      op: "suggest_missing_topic_tags_from_kb",
      limit: 24,
    });
    const tagsRaw = Array.isArray(out.tags) ? out.tags : [];
    return tagsRaw.map((t) => String(t || "").trim()).filter(Boolean);
  }

  async function addTopicCoverageTag(tag: string) {
    const topic = String(tag || "").trim();
    if (!topic) return;
    await postContactsDirectoryOp(p.organizationId, {
      op: "add_topic_coverage_tag",
      tag: topic,
    });
    p.toast(`Added topic coverage: ${topic}`);
    await p.load();
  }

  return { editExternal, openUserDirectoryEdit, runInviteLink, runMigrateManual, mergePath, deletePathTag, mergeTopic, deleteTopicTag, saveGeneralFallback, clearGeneralFallback, suggestMissingTopicTagsFromKb, addTopicCoverageTag };
}
